import { CommunityPostStatus } from 'generated/graphql';

// The community's `posts` field is cached once per argument set, and an
// optimistic write has to pick the variants it belongs to. The arguments are
// only reachable through the serialized field name, so parse them rather than
// substring-matching: a search term like "directions" or "DRAFT" would
// otherwise be read as pagination or as a status filter.
type PostsFieldArgs = {
  type?: string;
  lastId?: string;
  direction?: string;
  filters?: { statuses?: CommunityPostStatus[] };
};

export type FeedVariant = 'feed' | 'drafts' | 'scheduled' | 'other';

const argsOf = (storeFieldName: string): PostsFieldArgs => {
  const start = storeFieldName.indexOf('(');
  if (start === -1 || !storeFieldName.endsWith(')')) return {};
  try {
    return JSON.parse(storeFieldName.slice(start + 1, -1)) as PostsFieldArgs;
  } catch {
    return {};
  }
};

const carries = (args: PostsFieldArgs, status: CommunityPostStatus): boolean =>
  args.filters?.statuses?.includes(status) ?? false;

// A written post belongs on a first page only. Cursor-paginated pages and the
// pinned list are left alone.
const isFirstPage = (args: PostsFieldArgs): boolean =>
  args.lastId === undefined &&
  args.direction === undefined &&
  args.type === undefined;

export const feedVariantOf = (storeFieldName: string): FeedVariant => {
  const args = argsOf(storeFieldName);
  const drafts = carries(args, CommunityPostStatus.Draft);
  const scheduled = carries(args, CommunityPostStatus.Scheduled);

  if (!isFirstPage(args)) return 'other';
  // A mixed filter is nobody's queue: it belongs to whoever asked for it, not
  // to an optimistic write.
  if (drafts && scheduled) return 'other';
  if (drafts) return 'drafts';
  if (scheduled) return 'scheduled';
  return carries(args, CommunityPostStatus.Processing) ||
    args.filters?.statuses === undefined ||
    carries(args, CommunityPostStatus.Published)
    ? 'feed'
    : 'other';
};

export const variantForStatus = (
  status: string | null | undefined,
): FeedVariant => {
  if (status === CommunityPostStatus.Draft) return 'drafts';
  if (status === CommunityPostStatus.Scheduled) return 'scheduled';
  return 'feed';
};
